/* eslint-disable react/require-default-props */

import React, { FC, JSX, ReactNode, useCallback, useMemo } from 'react';

import { Icons, Loading } from '@sede-enterprise/shell-ds-react-framework';

import dayjs from 'dayjs';
import JSONPretty from 'react-json-pretty';

import Table from '../Table';
import Button from '../../../../UI/Button';
import Loader from '../../../../UI/Loader';
import { Modal } from '../../../../UI/Modal';
import useGetFieldAuditLogs from '../../../../hooks/nominations/useGetFieldAuditLogs';
import {
  IFieldAuditLogs,
  IFieldType,
} from '../../../../types/FieldNominations.type';
import { AuditDetailWrapper, ModalFooter } from '../style';

const { InfoCircle: IconCircleInfo, ChevronDown, ChevronRight } = Icons;

interface FieldAuditLogsProps {
  open: boolean;
  onClose: () => void;
  url: string;
  children: ReactNode;
  fieldType: IFieldType;
  modalTitle?: string;
}

const FieldAuditLogs: FC<FieldAuditLogsProps> = ({
  open,
  onClose,
  modalTitle = 'Field Nomination Info and Audit Logs',
  url,
  fieldType,
  children,
}) => {
  const {
    hasLoading,
    auditLogs,
    resetLogsOnCloseModal,
    onExpandLogs,
    onPayloadLogs,
  } = useGetFieldAuditLogs({
    url,
    hasLogs: open,
    fieldType,
  });

  const handleOnClose = useCallback(() => {
    resetLogsOnCloseModal();
    onClose();
  }, [resetLogsOnCloseModal, onClose]);

  const renderFooter = useMemo(
    (): JSX.Element => (
      <ModalFooter>
        <Button variant='outlined' onClick={handleOnClose} data-testid='Close'>
          Close
        </Button>
      </ModalFooter>
    ),
    [handleOnClose]
  );

  const renderMessage = (item: IFieldAuditLogs): JSX.Element => {
    return (
      <>
        <div>
          <span>{item.note}</span>
          {fieldType === IFieldType.FIELD_NORMS && item?.payload && (
            <Button
              data-testid='info-button'
              size='small'
              className='logs-info'
              onClick={() => onPayloadLogs?.(item)}
              key={`info-btn-${item.id}`}
              icon={item.setaPayloadLoading ? <Loading /> : <IconCircleInfo />}
              iconOnly
            />
          )}
        </div>
        {item.expanded && (
          <div>
            {item.setaPayload && Object.keys(item.setaPayload).length > 0 && (
              <JSONPretty data={item.setaPayload} />
            )}
          </div>
        )}
      </>
    );
  };

  const renderExpandIcon = (item: IFieldAuditLogs): JSX.Element | null => {
    if (item?.setaPayload === null) {
      return null;
    }
    return (
      <Button
        iconOnly
        icon={item.expanded ? <ChevronDown /> : <ChevronRight />}
        size='small'
        variant='transparent'
        onClick={() => onExpandLogs(item.id)}
        data-testid={`expand-icon-${item.id}`}
      />
    );
  };

  const renderTimestamp = (val) => (
    <div>{`${dayjs(val).format('ddd DD-MMM-YY hh:mm:ss A')}`}</div>
  );

  const columns = [
    {
      accessorKey: 'expanded',
      header: '',
      cell: ({ row }) => renderExpandIcon(row?.original),
      size: 30,
    },
    {
      accessorKey: 'note',
      header: 'Message',
      cell: ({ row }) => renderMessage(row?.original),
    },
    {
      accessorKey: 'createdDateTimeinGMT',
      header: 'Timestamp',
      size: 220,
      cell: ({ getValue }) => renderTimestamp(getValue()),
    },
  ];

  return (
    <Modal
      open={open}
      onClose={handleOnClose}
      title={modalTitle}
      isDraggable
      maskClosable={false}
      footer={renderFooter}
      width={700}
    >
      <AuditDetailWrapper>
        {children}
        {hasLoading ? (
          <Loader />
        ) : (
          <Table
            columns={columns}
            data={auditLogs}
            size='small'
            pagination={false}
            maxHeight={600}
          />
        )}
      </AuditDetailWrapper>
    </Modal>
  );
};

export default FieldAuditLogs;
